/**
 * Magic-Link-Auth Hook (Stub).
 *
 * In AP6 ist die Supabase-Integration noch nicht verdrahtet — der
 * Backend-Spezialist baut sie parallel. Dieser Hook hält ein Mock-User-Objekt
 * im Speicher (+ localStorage, damit Reloads funktionieren) und erlaubt der UI,
 * gegen die finale API-Form zu entwickeln.
 *
 * Die Signatur entspricht dem geplanten Supabase-Helper. Der Konsolidierungs-
 * Schritt ersetzt die Implementierung, ohne dass Komponenten geändert werden
 * müssen.
 */

import { useEffect, useSyncExternalStore } from 'react';

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly displayName?: string | undefined;
}

interface AuthState {
  readonly user: AuthUser | null;
  readonly status: 'unauthenticated' | 'pending' | 'authenticated';
  readonly lastMagicLinkEmail: string | null;
}

const STORAGE_KEY = 'tragfaehigkeit:auth-mock';

let state: AuthState = loadFromStorage();
const listeners = new Set<() => void>();

function loadFromStorage(): AuthState {
  if (typeof window === 'undefined') {
    return { user: null, status: 'unauthenticated', lastMagicLinkEmail: null };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { user: null, status: 'unauthenticated', lastMagicLinkEmail: null };
    }
    const parsed = JSON.parse(raw) as AuthState;
    return parsed;
  } catch {
    return { user: null, status: 'unauthenticated', lastMagicLinkEmail: null };
  }
}

function persist(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage quota / private mode – wir verlieren in dem Fall den State über
    // den Reload, das ist für den AP6-Stub akzeptabel.
  }
}

function notify(): void {
  for (const l of listeners) l();
}

function setState(next: AuthState): void {
  state = next;
  persist();
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): AuthState {
  return state;
}

function getServerSnapshot(): AuthState {
  return { user: null, status: 'unauthenticated', lastMagicLinkEmail: null };
}

/**
 * useAuth – React-Hook für Auth-State.
 *
 * Im AP6-Stub: Magic-Link wird simuliert. `signIn(email)` nimmt jede
 * E-Mail an und legt den User direkt an (kein echter E-Mail-Versand). Die UI
 * zeigt einen „Bitte Postfach prüfen"-Screen, simuliert dann den Link-Klick
 * über `confirmMagicLink()`.
 */
export function useAuth(): {
  user: AuthUser | null;
  status: AuthState['status'];
  lastMagicLinkEmail: string | null;
  signIn: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmMagicLink: () => void;
  signOut: () => void;
} {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return {
    user: snap.user,
    status: snap.status,
    lastMagicLinkEmail: snap.lastMagicLinkEmail,
    signIn: async (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        throw new Error('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      }
      // Simulierter „Wir verschicken Mail"-Roundtrip.
      await new Promise((r) => setTimeout(r, 250));
      setState({ ...state, status: 'pending', lastMagicLinkEmail: trimmed });
    },
    // Mock: jedes Passwort ab 1 Zeichen wird akzeptiert und authentifiziert
    // direkt (kein Mail-Roundtrip). Die Rollen-Ableitung passiert wie beim
    // Magic-Link über die E-Mail (deriveRoleFromEmail in useUserRole).
    signInWithPassword: async (email: string, password: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        throw new Error('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      }
      if (password.length === 0) {
        throw new Error('Bitte geben Sie ein Passwort ein.');
      }
      await new Promise((r) => setTimeout(r, 250));
      setState({
        user: { id: `mock-${trimmed}`, email: trimmed },
        status: 'authenticated',
        lastMagicLinkEmail: null,
      });
    },
    // Mock: Signup authentifiziert direkt (überspringt die Bestätigungs-Mail),
    // damit der Dev-Klickpfad ohne Postfach funktioniert.
    signUpWithPassword: async (email: string, password: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed.includes('@')) {
        throw new Error('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      }
      if (password.length === 0) {
        throw new Error('Bitte geben Sie ein Passwort ein.');
      }
      await new Promise((r) => setTimeout(r, 250));
      setState({
        user: { id: `mock-${trimmed}`, email: trimmed },
        status: 'authenticated',
        lastMagicLinkEmail: null,
      });
    },
    // Mock: no-op — im Dev-Modus gibt es kein Postfach.
    requestPasswordReset: async () => {
      await new Promise((r) => setTimeout(r, 250));
    },
    confirmMagicLink: () => {
      const email = state.lastMagicLinkEmail;
      if (!email) return;
      setState({
        user: { id: `mock-${email}`, email },
        status: 'authenticated',
        lastMagicLinkEmail: null,
      });
    },
    signOut: () => {
      setState({ user: null, status: 'unauthenticated', lastMagicLinkEmail: null });
    },
  };
}

/**
 * Test-Helper: setzt den Modul-State des Mocks zurück (der überlebt sonst
 * zwischen Tests derselben Datei, weil er auf Modulebene liegt).
 * Nur in Vitest verwenden.
 */
export function __resetAuthMockForTests(): void {
  setState({ user: null, status: 'unauthenticated', lastMagicLinkEmail: null });
}

/**
 * useAuthIsHydrated – stellt sicher, dass die initiale localStorage-Lese
 * passiert ist (relevant für SSR-Szenarien; im AP6-Stub no-op).
 */
export function useAuthIsHydrated(): boolean {
  useEffect(() => {
    // hier könnte man später Supabase.auth.getSession() abwarten
  }, []);
  return true;
}
